import { useEffect, useState } from "react";
import { useAuth } from "../lib/auth";
import api from "../lib/api";
import Header from "../components/Header";
import ThisWeekHero from "../components/ThisWeekHero";
import NextUpCard from "../components/NextUpCard";
import Timeline from "../components/Timeline";
import Announcements from "../components/Announcements";
import ExhibitionCard from "../components/ExhibitionCard";
import ShareCard from "../components/ShareCard";
import HighlightsCard from "../components/HighlightsCard";
import { motion } from "framer-motion";
import { Books, ChartLine } from "@phosphor-icons/react";

export default function Dashboard() {
  const { user } = useAuth();
  const [weeks, setWeeks] = useState([]);
  const [currentWeek, setCurrentWeek] = useState(1);
  const [announcements, setAnnouncements] = useState([]);
  const [progress, setProgress] = useState([]);
  const [loading, setLoading] = useState(true);

  const isTeacher = user?.role === "teacher";

  const load = async () => {
    setLoading(true);
    const [w, c, a, p] = await Promise.all([
      api.get("/weeks"),
      api.get("/current-week"),
      api.get("/announcements"),
      api.get("/progress").catch(() => ({ data: [] })),
    ]);
    setWeeks(w.data);
    setCurrentWeek(c.data.week_num);
    setAnnouncements(a.data);
    setProgress(p.data || []);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const setCurrentWeekNum = async (week_num) => {
    await api.post("/current-week", { week_num });
    setCurrentWeek(week_num);
  };

  const createAnnouncement = async (message, publish_at) => {
    const { data } = await api.post("/announcements", { message, publish_at });
    setAnnouncements([data, ...announcements]);
  };

  const deleteAnnouncement = async (id) => {
    await api.delete(`/announcements/${id}`);
    setAnnouncements(announcements.filter((a) => a.id !== id));
  };

  const thisWeek = weeks.find((w) => w.week_num === currentWeek);
  const nextWeek = weeks.find((w) => w.week_num === currentWeek + 1);

  // Progress stats for student — distinguish verified from pending so the
  // dashboard pill matches what the teacher sees in the roster.
  const totalTasks = weeks.reduce((sum, w) => sum + (w.tasks?.length || 0), 0);
  const verifiedCount = progress.filter((p) => p.verified_at).length;
  const pendingCount = progress.length - verifiedCount;
  const pct = totalTasks ? Math.round((verifiedCount / totalTasks) * 100) : 0;

  return (
    <div className="min-h-screen bg-[#FFFDF8]">
      <Header />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8 space-y-6">
        {loading ? (
          <div className="text-slate-500" data-testid="dashboard-loading">Loading your class...</div>
        ) : (
          <>
            <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
              <div className="flex items-center justify-between flex-wrap gap-3 mb-1">
                <h1 className="font-outfit text-3xl sm:text-4xl font-black tracking-tight" data-testid="dashboard-greeting">
                  Hey, <span className="text-yellow-600">{user?.name?.split(" ")[0]}</span>.
                </h1>
                <div className="text-sm text-slate-500">
                  {isTeacher ? "Teacher view · you can edit weeks and post notes." : "Let's keep building."}
                </div>
              </div>
              <p className="text-slate-600">Here's what's next in the Alpha AI Discovery Club.</p>
            </motion.div>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
              <div className="lg:col-span-8 space-y-6">
                <ThisWeekHero week={thisWeek} currentWeekNum={currentWeek} />
                {nextWeek && currentWeek < 10 && <NextUpCard week={nextWeek} />}
                {currentWeek === 10 && <ExhibitionCard week={thisWeek} />}
              </div>

              <aside className="lg:col-span-4 space-y-6">
                {!isTeacher && (
                  <div className="bg-white rounded-3xl p-6 border border-slate-200 shadow-sm" data-testid="progress-card">
                    <div className="flex items-center gap-2 mb-3">
                      <ChartLine weight="fill" className="text-emerald-500" size={20} />
                      <h3 className="font-outfit font-bold text-xl">Your progress</h3>
                    </div>
                    <div className="text-4xl font-outfit font-black">{pct}%</div>
                    <div className="text-sm text-slate-500 mt-1">
                      {verifiedCount} of {totalTasks} verified
                      {pendingCount > 0 && (
                        <span className="ml-2 inline-flex items-center gap-0.5 text-amber-700 font-medium" data-testid="dashboard-pending-pill">
                          · {pendingCount} pending review
                        </span>
                      )}
                    </div>
                    <div className="mt-3 h-3 bg-slate-100 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-emerald-500 rounded-full transition-all"
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                  </div>
                )}

                {isTeacher && (
                  <div className="bg-white rounded-3xl p-6 border border-slate-200 shadow-sm" data-testid="teacher-stats-card">
                    <div className="flex items-center gap-2 mb-3">
                      <Books weight="fill" className="text-blue-500" size={20} />
                      <h3 className="font-outfit font-bold text-xl">Class status</h3>
                    </div>
                    <div className="text-sm text-slate-600">
                      Currently teaching <span className="font-bold text-slate-900">Week {currentWeek}</span>.
                    </div>
                    <div className="text-sm text-slate-500 mt-1">
                      Tap any week below to set it as the current focus.
                    </div>
                  </div>
                )}

                {isTeacher && <ShareCard />}

                <Announcements
                  items={announcements}
                  isTeacher={isTeacher}
                  onCreate={createAnnouncement}
                  onDelete={deleteAnnouncement}
                />
              </aside>
            </div>

            <Timeline
              weeks={weeks}
              currentWeek={currentWeek}
              isTeacher={isTeacher}
              onSetCurrent={setCurrentWeekNum}
            />

            <HighlightsCard />

            {currentWeek !== 10 && <ExhibitionCard week={weeks.find((w) => w.week_num === 10)} />}
          </>
        )}
      </main>
    </div>
  );
}
